import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowUp, ArrowDown, Save, LogOut, Palette, Image as ImageIcon, Layers, X, Lock, Link2, Clapperboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useContent } from "@/context/ContentContext";

/* ---- helpers colore hex <-> "H S% L%" ---- */
function hexToHslString(hex) {
  let r = 0, g = 0, b = 0;
  hex = hex.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  r = parseInt(hex.slice(0, 2), 16) / 255;
  g = parseInt(hex.slice(2, 4), 16) / 255;
  b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
function hslStringToHex(hsl) {
  const m = /([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/.exec(hsl || "");
  if (!m) return "#2563eb";
  let h = +m[1] / 360, s = +m[2] / 100, l = +m[3] / 100;
  const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; };
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else { const q = l < 0.5 ? l * (1 + s) : l + s - l * s; const p = 2 * l - q; r = hue2rgb(p, q, h + 1 / 3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1 / 3); }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const THEME_DEFAULTS = { primary: "221 83% 53%", cta: "187 84% 62%", navy: "226 47% 9%" };
const THEME_LABELS = { primary: "Colore primario", cta: "Accento CTA", navy: "Sfondo scuro" };

const CTA_LINKS = [
  { path: "nav.cta.link", label: "Navbar - Prova gratis", def: "https://app.sedio.it" },
  { path: "hero.ctaPrimary.link", label: "Hero - pulsante principale", def: "https://app.sedio.it" },
  { path: "hero.ctaSecondary.link", label: "Hero - pulsante secondario", def: "funzioni" },
  { path: "benefits.cta.link", label: "Perche Sedio - CTA", def: "https://app.sedio.it" },
  { path: "cash.cta.link", label: "Programma Partner - CTA", def: "https://app.sedio.it" },
  { path: "save.cta.link", label: "Risparmio - CTA", def: "https://app.sedio.it" },
  { path: "trial.cta.link", label: "Prova gratuita - CTA", def: "https://app.sedio.it" },
  { path: "contact.cta.link", label: "Hai domande? - Prova gratis", def: "https://app.sedio.it" },
  { path: "mid.cta.link", label: "CTA intermedia", def: "https://app.sedio.it" },
  { path: "footer.cta.link", label: "Footer - Contattaci", def: "contatti" },
];

const VIDEO_LINKS = [
  { path: "video.posta.url", label: "Posta intelligente" },
  { path: "video.chat.url", label: "Chat AI" },
  { path: "video.catasto.url", label: "Aggiornamento catastale" },
  { path: "video.stime.url", label: "Stime immobili" },
  { path: "video.pratiche.url", label: "Pratiche AI" },
];

function LoginGate() {
  const { login } = useContent();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Accesso effettuato", { description: "Editor attivo. Clicca sui testi per modificarli." });
    } catch (err) {
      toast.error("Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" data-testid="builder-login">
        <div className="mb-4 flex items-center gap-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Lock className="h-5 w-5" /></span>
          <div>
            <h2 className="font-display text-lg font-bold text-foreground">Editor Sedio</h2>
            <p className="text-xs text-muted-foreground">Inserisci utente e password per modificare il sito.</p>
          </div>
        </div>
        <Label htmlFor="usr">Utente</Label>
        <Input id="usr" value={username} onChange={(e) => setUsername(e.target.value)} className="mb-3 mt-1.5" data-testid="builder-login-username" autoFocus autoComplete="username" />
        <Label htmlFor="pwd">Password</Label>
        <Input id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="builder-login-password" autoComplete="current-password" />
        <Button type="submit" disabled={loading} className="btn-cta mt-4 h-11 w-full" data-testid="builder-login-submit">
          {loading ? "Accesso..." : "Entra nell'editor"}
        </Button>
        <a href="/" className="mt-3 block text-center text-xs text-muted-foreground hover:text-foreground">Esci dalla modalita editor</a>
      </form>
    </div>
  );
}

export function BuilderToolbar() {
  const { editMode, authed, dirty, saving, save, logout, sections, toggleSection, moveSection, theme, setThemeVar, getText, setText } = useContent();
  const [panel, setPanel] = useState(null); // 'theme' | 'images' | 'sections'

  if (!editMode) return null;
  if (!authed) return <LoginGate />;

  const doSave = async () => {
    try {
      await save();
      toast.success("Salvato e pubblicato", { description: "Le modifiche sono ora online per tutti." });
    } catch (e) {
      toast.error("Salvataggio non riuscito", { description: "Riprova tra poco." });
    }
  };

  const exit = () => {
    window.location.href = "/";
  };

  return (
    <>
      {/* Barra inferiore */}
      <div className="fixed bottom-4 left-1/2 z-[90] flex -translate-x-1/2 items-center gap-1 rounded-full border border-white/10 bg-[hsl(226_47%_10%)] px-2 py-2 text-white shadow-2xl" data-testid="builder-toolbar">
        <span className="ml-2 mr-1 hidden text-xs font-semibold sm:inline">Editor</span>
        <ToolbarBtn active={panel === "sections"} onClick={() => setPanel(panel === "sections" ? null : "sections")} icon={Layers} label="Sezioni" />
        <ToolbarBtn active={panel === "theme"} onClick={() => setPanel(panel === "theme" ? null : "theme")} icon={Palette} label="Colori" />
        <ToolbarBtn active={panel === "images"} onClick={() => setPanel(panel === "images" ? null : "images")} icon={ImageIcon} label="Immagini" />
        <ToolbarBtn active={panel === "links"} onClick={() => setPanel(panel === "links" ? null : "links")} icon={Link2} label="Link" />
        <ToolbarBtn active={panel === "videos"} onClick={() => setPanel(panel === "videos" ? null : "videos")} icon={Clapperboard} label="Video" />
        <div className="mx-1 h-6 w-px bg-white/15" />
        <Button onClick={doSave} disabled={saving} className="btn-cta h-9 gap-1.5 px-4" data-testid="builder-save">
          <Save className="h-4 w-4" /> {saving ? "Salvo..." : dirty ? "Salva" : "Salvato"}
        </Button>
        <button onClick={() => { logout(); exit(); }} className="ml-1 inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm text-slate-300 hover:bg-white/10 hover:text-white" data-testid="builder-exit">
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </div>

      {/* Pannello */}
      {panel && (
        <div className="fixed bottom-24 left-1/2 z-[90] w-[min(92vw,420px)] -translate-x-1/2 rounded-2xl border border-border bg-white p-4 shadow-2xl" data-testid={`builder-panel-${panel}`}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-sm font-bold text-foreground">
              {panel === "sections" ? "Sezioni della pagina" : panel === "theme" ? "Colori del tema" : panel === "links" ? "Link dei bottoni" : panel === "videos" ? "Video delle funzioni" : "Immagini"}
            </h3>
            <button onClick={() => setPanel(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>

          {panel === "sections" && (
            <div className="max-h-[50vh] space-y-1.5 overflow-auto pr-1">
              {sections.map((s, i) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <span className={`text-sm ${s.visible ? "text-foreground" : "text-muted-foreground line-through"}`}>{s.label}</span>
                  <div className="flex items-center gap-1">
                    <IconMini onClick={() => moveSection(s.id, -1)} disabled={i === 0} icon={ArrowUp} />
                    <IconMini onClick={() => moveSection(s.id, 1)} disabled={i === sections.length - 1} icon={ArrowDown} />
                    <IconMini onClick={() => toggleSection(s.id)} icon={s.visible ? Eye : EyeOff} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {panel === "theme" && (
            <div className="space-y-3">
              {Object.keys(THEME_DEFAULTS).map((k) => (
                <div key={k} className="flex items-center justify-between">
                  <Label className="text-sm">{THEME_LABELS[k]}</Label>
                  <input
                    type="color"
                    value={hslStringToHex(theme[k] || THEME_DEFAULTS[k])}
                    onChange={(e) => setThemeVar(k, hexToHslString(e.target.value))}
                    className="h-9 w-14 cursor-pointer rounded border border-border bg-white"
                    data-testid={`builder-color-${k}`}
                  />
                </div>
              ))}
              <p className="text-xs text-muted-foreground">Le anteprime si aggiornano subito. Ricordati di salvare.</p>
            </div>
          )}

          {panel === "images" && (
            <div className="space-y-3">
              <div>
                <Label className="text-sm">URL immagine hero</Label>
                <Input value={getText("hero.image", "")} onChange={(e) => setText("hero.image", e.target.value)} placeholder="https://..." className="mt-1.5" data-testid="builder-img-hero" />
              </div>
              <div>
                <Label className="text-sm">URL logo</Label>
                <Input value={getText("logo.image", "")} onChange={(e) => setText("logo.image", e.target.value)} placeholder="/sedio-logo.png" className="mt-1.5" data-testid="builder-img-logo" />
              </div>
              <p className="text-xs text-muted-foreground">Incolla un link immagine pubblico. Lascia vuoto per usare il default.</p>
            </div>
          )}

          {panel === "links" && (
            <div className="max-h-[50vh] space-y-3 overflow-auto pr-1">
              {CTA_LINKS.map((c) => (
                <div key={c.path}>
                  <Label className="text-sm">{c.label}</Label>
                  <Input
                    value={getText(c.path, "")}
                    onChange={(e) => setText(c.path, e.target.value)}
                    placeholder={c.def}
                    className="mt-1.5"
                    data-testid={`builder-link-${c.path}`}
                  />
                </div>
              ))}
              <p className="text-xs leading-5 text-muted-foreground">
                Usa il nome di una sezione (<b>contatti</b>, <b>funzioni</b>, <b>risparmio</b>, <b>come-funziona</b>, <b>faq</b>) per scorrere nella pagina, oppure un link esterno <b>https://...</b>, <b>mailto:email</b> o <b>tel:+39...</b>. Vuoto = destinazione di default.
              </p>
            </div>
          )}
          {panel === "videos" && (
            <div className="max-h-[50vh] space-y-3 overflow-auto pr-1">
              {VIDEO_LINKS.map((v) => (
                <div key={v.path}>
                  <Label className="text-sm">{v.label}</Label>
                  <Input
                    value={getText(v.path, "")}
                    onChange={(e) => setText(v.path, e.target.value)}
                    placeholder="https://youtube.com/... , vimeo, loom o link .mp4"
                    className="mt-1.5"
                    data-testid={`builder-video-${v.path}`}
                  />
                </div>
              ))}
              <p className="text-xs leading-5 text-muted-foreground">
                Incolla il link del video (YouTube, Vimeo, Loom o file <b>.mp4</b>). Lascia vuoto per mostrare il segnaposto "Video in arrivo".
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function ToolbarBtn({ icon: Icon, label, onClick, active }) {
  return (
    <button onClick={onClick} className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm transition-colors ${active ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
      <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
function IconMini({ icon: Icon, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-30">
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
